import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Tooltip, Typography } from "antd";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { DeviceLocationTableRecord } from "../../types";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { ReactComponent as MosquitoIconSvg } from "../../assets/icons/MosquitoVector.svg";
import ArchiveBoxIcon from "@heroicons/react/24/outline/ArchiveBoxIcon";
import "./index.css";
import {
  useMaintenanceAlertsCounts,
  useDetectionAlertsCounts,
} from "../../hooks/useAppAlerts";
import { ROUTES } from "../../config/routes";
import { generatePath } from "react-router-dom";

const { Title } = Typography;

const TrapLocationTable: React.FC<{}> = () => {
  const navigate = useNavigate();
  const { data: trapSiteData, isLoading: isTrapSitesLoading } =
    useListTrapSitesQuery();
  const { data: devices, isLoading: isDevicesLoading } = useListDevicesQuery();

  const { counts: maintenanceCounts, isLoading: isMaintenanceLoading } =
    useMaintenanceAlertsCounts();
  const { counts: detectionCounts, isLoading: isDetectionLoading } =
    useDetectionAlertsCounts();

  const deviceData = useMemo((): DeviceLocationTableRecord[] => {
    if (!devices?.items || !trapSiteData?.items) return [];

    const trapSiteMap = new Map(trapSiteData.items.map((t) => [t._id, t]));

    return devices.items.map((device) => {
      const trapSite = trapSiteMap.get(device.trap_id);

      return {
        key: device.uuid || "",
        trapId: device.trap_id || "unassigned-location",
        deviceName: device.display_name || "N/A",
        location: trapSite?.display_name || "N/A",
        address: trapSite?.address || "N/A",
        maintenanceAlerts: maintenanceCounts[device?.uuid || ""] || 0,
        detectionAlerts: detectionCounts[device?.uuid || ""] || 0,
      };
    });
  }, [devices, trapSiteData, maintenanceCounts, detectionCounts]);

  const sortIcon = useMemo(
    () =>
      ({ sortOrder }: { sortOrder: "ascend" | "descend" | null }) => {
        if (sortOrder === "ascend")
          return <ChevronUpIcon className="w-5 h-5 text-[#003A55] ml-1" />;
        if (sortOrder === "descend")
          return <ChevronDownIcon className="w-5 h-5 text-[#003A55] ml-1" />;
        return (
          <ChevronDownIcon className="w-5 h-5 text-[#003A55] opacity-30 ml-1" />
        );
      },
    [],
  );

  const columns = useMemo(
    () => [
      {
        title: "Trap ID",
        dataIndex: "deviceName",
        key: "deviceName",
        sorter: (a: DeviceLocationTableRecord, b: DeviceLocationTableRecord) =>
          (a.deviceName || "").localeCompare(b.deviceName || ""),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        sorter: (a: DeviceLocationTableRecord, b: DeviceLocationTableRecord) =>
          a.location.localeCompare(b.location),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Address",
        dataIndex: "address",
        key: "address",
        sorter: (a: DeviceLocationTableRecord, b: DeviceLocationTableRecord) =>
          a.address.localeCompare(b.address),
        sortIcon,
        render: (text: string) => (
          <Tooltip title={text} placement="topLeft">
            <span className="block truncate">{text}</span>
          </Tooltip>
        ),
      },
      {
        title: (
          <span className="flex items-center justify-center">
            <MosquitoIconSvg
              width={24}
              height={24}
              fill="#0C4A6E"
              className="opacity-80"
            />
          </span>
        ),
        key: "detectionAlert",
        dataIndex: "detectionAlerts",
        width: 60,
        align: "center" as const,
        render: (text: string) => text,
      },
      {
        title: (
          <span className="flex items-center justify-center">
            <ArchiveBoxIcon
              className="w-6 h-6 text-[#0C4A6E]"
              title="Maintenance Alert"
            />
          </span>
        ),
        key: "maintenanceAlert",
        dataIndex: "maintenanceAlerts",
        width: 60,
        align: "center" as const,
        render: (text: string) => text,
      },
    ],
    [sortIcon],
  );

  return (
    <div className="bg-white p-4 rounded-lg border border-[#0C4A6E]">
      <Title className="trap-locations-title" level={4}>
        Trap locations
      </Title>
      <Table<DeviceLocationTableRecord>
        columns={columns}
        dataSource={deviceData}
        loading={
          isTrapSitesLoading ||
          isDevicesLoading ||
          isMaintenanceLoading ||
          isDetectionLoading
        }
        pagination={false}
        scroll={{ y: "max-content" }}
        className="device-table"
        onRow={(record) => ({
          onClick: () =>
            navigate(
              generatePath(ROUTES.location_detail, { id: record.trapId }),
            ),
          style: { cursor: "pointer" },
        })}
      />
    </div>
  );
};

export default TrapLocationTable;
